import { LimitScope, OrganizationLimitType } from "../../../../gql/graphql";
import {
  formatLimitValue,
  getProgressColor,
  isOrganizationLimit,
  isResourceScopedLimit,
  LimitInfo,
  useOrganizationLimits,
} from "../../../../business/limits.hook";
import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Group,
  Progress,
  rem,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
  alpha,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { useNavigate } from "react-router-dom";
import {
  IconArrowRight,
  IconCheck,
  IconClock,
  IconGauge,
  IconSearch,
  IconServer,
  IconSparkles,
  IconToggleLeft,
} from "@tabler/icons-react";
import { PageHeader } from "../../components/PageHeader";
import { DetailPageSkeleton } from "../../components/SkeletonLoader";
import { EmptyState } from "../../components/EmptyState";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = createStyles((theme) => ({
  page: {
    maxWidth: rem(1200),
  },

  searchInput: {
    maxWidth: rem(320),
  },

  // Summary stats row
  summaryCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    textAlign: "center" as const,
  },
  summaryValue: {
    fontSize: rem(28),
    fontWeight: 800,
    lineHeight: 1,
    color: theme.colors.dark[0],
  },
  summaryLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 500,
    color: theme.colors.dark[3],
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },

  // Limit card
  limitCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    transition: "border-color 200ms ease, box-shadow 200ms ease",
    "&:hover": {
      borderColor: theme.colors.dark[4],
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
    },
  },

  limitName: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    color: theme.colors.dark[0],
    lineHeight: 1.3,
  },
  limitDescription: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
    lineHeight: 1.4,
  },

  usageRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
  },
  usageValue: {
    fontSize: rem(20),
    fontWeight: 700,
    lineHeight: 1,
    color: theme.colors.dark[0],
  },
  usageMax: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
  },
  usagePct: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
  },

  // Boolean limit card
  booleanValue: {
    fontSize: rem(16),
    fontWeight: 700,
  },

  // Days limit card
  daysValue: {
    fontSize: rem(20),
    fontWeight: 700,
    lineHeight: 1,
    color: theme.colors.dark[0],
  },

  // Type badge
  typeBadge: {
    fontSize: rem(10),
    fontWeight: 600,
    color: theme.colors.dark[3],
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    padding: `${rem(2)} ${rem(6)}`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.colors.dark[6],
    border: `1px solid ${theme.colors.dark[5]}`,
  },

  // CTA
  ctaCard: {
    backgroundColor: alpha(theme.colors.cyan[9], 0.06),
    border: `1px solid ${alpha(theme.colors.cyan[5], 0.2)}`,
  },

  sectionTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    color: theme.colors.dark[1],
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },

  noResults: {
    padding: `${theme.spacing.xl} 0`,
    textAlign: "center" as const,
  },
}));

// ---------------------------------------------------------------------------
// Limit Card Components
// ---------------------------------------------------------------------------

type LimitCardClasses = ReturnType<typeof useStyles>["classes"];

interface LimitCardProps {
  limit: LimitInfo;
  classes: LimitCardClasses;
}

const LimitCardHeader = ({ limit, classes }: LimitCardProps) => (
  <Group justify="space-between" align="flex-start" wrap="nowrap">
    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
      <Tooltip label={limit.description} multiline maw={300}>
        <Text className={classes.limitName} lineClamp={1}>
          {limit.name}
        </Text>
      </Tooltip>
    </Stack>
    <Text className={classes.typeBadge}>
      {limit.scope === LimitScope.Organization ? "org" : "resource"}
    </Text>
  </Group>
);

const NumberLimitCard = ({ limit, classes }: LimitCardProps) => {
  if (isResourceScopedLimit(limit)) {
    return (
      <Card className={classes.limitCard} p="md" radius="lg">
        <Stack gap="sm">
          <LimitCardHeader limit={limit} classes={classes} />
          <Group justify="space-between">
            <Stack gap={0}>
              <Text size="xs" c="dimmed">
                Limit
              </Text>
              <Tooltip label={limit.max.toLocaleString()}>
                <Text className={classes.usageValue}>
                  {formatLimitValue(limit.max, limit.type)}
                </Text>
              </Tooltip>
            </Stack>
            <Stack gap={0} align="flex-end">
              <Text size="xs" c="dimmed">
                Default
              </Text>
              <Text size="sm" c="dark.2">
                {formatLimitValue(limit.default, limit.type)}
              </Text>
            </Stack>
          </Group>
        </Stack>
      </Card>
    );
  }

  const color = getProgressColor(limit.pct);

  return (
    <Card className={classes.limitCard} p="md" radius="lg">
      <Stack gap="sm">
        <LimitCardHeader limit={limit} classes={classes} />
        <div className={classes.usageRow}>
          <Tooltip label={limit.current.toLocaleString()}>
            <Text className={classes.usageValue}>
              {formatLimitValue(limit.current, limit.type)}
            </Text>
          </Tooltip>
          <Tooltip label={limit.max.toLocaleString()}>
            <Text className={classes.usageMax}>
              / {formatLimitValue(limit.max, limit.type)}
            </Text>
          </Tooltip>
        </div>
        <Progress value={limit.pct} color={color} size="sm" radius="xl" />
        <Group justify="space-between">
          <Text className={classes.usagePct} c={color}>
            {limit.pct.toFixed(0)}% used
          </Text>
          <Text size="xs" c="dimmed">
            Default: {formatLimitValue(limit.default, limit.type)}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
};

const BooleanLimitCard = ({ limit, classes }: LimitCardProps) => {
  const isEnabled = limit.max === 1;

  return (
    <Card className={classes.limitCard} p="md" radius="lg">
      <Stack gap="sm">
        <LimitCardHeader limit={limit} classes={classes} />

        <Group justify="space-between" align="center">
          <Group gap="xs">
            <ThemeIcon
              size="md"
              radius="xl"
              variant="light"
              color={isEnabled ? "teal" : "dark"}
            >
              {isEnabled ? (
                <IconCheck size={14} />
              ) : (
                <IconToggleLeft size={14} />
              )}
            </ThemeIcon>
            <Text
              className={classes.booleanValue}
              color={isEnabled ? "teal.4" : "dark.3"}
            >
              {formatLimitValue(limit.max, limit.type)}
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Default: {formatLimitValue(limit.default, limit.type)}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
};

const DaysLimitCard = ({ limit, classes }: LimitCardProps) => {
  return (
    <Card className={classes.limitCard} p="md" radius="lg">
      <Stack gap="sm">
        <LimitCardHeader limit={limit} classes={classes} />

        <Group justify="space-between" align="center">
          <Group gap="xs">
            <ThemeIcon size="md" radius="xl" variant="light" color="violet">
              <IconClock size={14} />
            </ThemeIcon>
            <Text className={classes.daysValue}>
              {formatLimitValue(limit.max, limit.type)}
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Default: {formatLimitValue(limit.default, limit.type)}
          </Text>
        </Group>
      </Stack>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const Limits = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "org" | "resource">("all");

  const { limits, summary, loading, error } = useOrganizationLimits();

  const filteredLimits = useMemo(() => {
    let result = limits;
    if (filter === "org") {
      result = result.filter(isOrganizationLimit);
    } else if (filter === "resource") {
      result = result.filter(isResourceScopedLimit);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          l.description.toLowerCase().includes(q),
      );
    }
    return result;
  }, [limits, filter, search]);

  if (loading) {
    return <DetailPageSkeleton />;
  }

  if (error) {
    return (
      <Stack gap="xl" className={classes.page}>
        <PageHeader
          title="Limits"
          subtitle="Monitor your organization's resource usage and limits"
          showBreadcrumbs={false}
        />
        <EmptyState
          title="Failed to load limits"
          description="Please try refreshing the page."
          iconType="error"
          iconColor="rose"
        />
      </Stack>
    );
  }

  return (
    <Stack gap="xl" className={`${classes.page} wp-fade-in`}>
      {/* Page Header */}
      <PageHeader
        title="Limits"
        subtitle="Monitor your organization's resource usage and limits"
        showBreadcrumbs={false}
        actions={[
          {
            label: "Manage Subscription",
            icon: <IconSparkles size={16} />,
            onClick: () => navigate("/limits/subscription"),
          },
        ]}
      />

      {/* Summary Cards */}
      <SimpleGrid
        cols={{ base: 2, xs: 4, md: 4 }}
        className="wp-stagger-children"
      >
        <Card className={classes.summaryCard} p="md" radius="lg">
          <Stack gap={4} align="center">
            <Text className={classes.summaryValue}>{summary.total}</Text>
            <Text className={classes.summaryLabel}>Total Limits</Text>
          </Stack>
        </Card>
        <Card className={classes.summaryCard} p="md" radius="lg">
          <Stack gap={4} align="center">
            <Text className={classes.summaryValue} c="teal.4">
              {summary.healthy}
            </Text>
            <Text className={classes.summaryLabel}>Healthy</Text>
          </Stack>
        </Card>
        <Card className={classes.summaryCard} p="md" radius="lg">
          <Stack gap={4} align="center">
            <Text className={classes.summaryValue} c="amber.4">
              {summary.nearLimit}
            </Text>
            <Text className={classes.summaryLabel}>Near Limit</Text>
          </Stack>
        </Card>
        <Card className={classes.summaryCard} p="md" radius="lg">
          <Stack gap={4} align="center">
            <Text className={classes.summaryValue} c="rose.4">
              {summary.atCapacity}
            </Text>
            <Text className={classes.summaryLabel}>At Capacity</Text>
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Group justify="space-between">
        <SegmentedControl
          value={filter}
          onChange={(val) => setFilter(val as "all" | "org" | "resource")}
          data={[
            { label: "All", value: "all" },
            { label: "Organization", value: "org" },
            { label: "Resource", value: "resource" },
          ]}
          size="sm"
        />
        <TextInput
          className={classes.searchInput}
          placeholder="Search limits..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          size="sm"
        />
      </Group>

      {/* Limit Cards */}
      {filteredLimits.length === 0 ? (
        <EmptyState
          title="No limits found"
          description="Try adjusting your search or filter."
          iconType="search"
          compact
        />
      ) : (
        <Box>
          <SimpleGrid
            cols={{ base: 1, sm: 3, lg: 3 }}
            className="wp-stagger-children"
          >
            {filteredLimits.map((limit) => {
              if (limit.type === OrganizationLimitType.Boolean) {
                return (
                  <BooleanLimitCard
                    key={limit.id}
                    limit={limit}
                    classes={classes}
                  />
                );
              }

              if (limit.type === OrganizationLimitType.Days) {
                return (
                  <DaysLimitCard
                    key={limit.id}
                    limit={limit}
                    classes={classes}
                  />
                );
              }

              return (
                <NumberLimitCard
                  key={limit.id}
                  limit={limit}
                  classes={classes}
                />
              );
            })}
          </SimpleGrid>
        </Box>
      )}

      {/* CTA */}
      <Card className={classes.ctaCard} p="lg" radius="lg">
        <Group justify="space-between" align="center">
          <Stack gap={4}>
            <Text fw={600} c="cyan.3">
              Need higher limits?
            </Text>
            <Text size="sm" c="dimmed">
              Upgrade your subscription to unlock more capacity for your
              organization.
            </Text>
          </Stack>
          <Button
            variant="light"
            color="cyan"
            rightSection={<IconArrowRight size={16} />}
            className="wp-btn-press"
            onClick={() => navigate("/limits/subscription")}
          >
            View Packages
          </Button>
        </Group>
      </Card>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Home Page Styles
// ---------------------------------------------------------------------------

const useHomeStyles = createStyles((theme) => ({
  page: {
    maxWidth: rem(1200),
  },

  // Hero section
  heroCard: {
    backgroundColor: alpha(theme.colors.cyan[9], 0.04),
    border: `1px solid ${alpha(theme.colors.cyan[5], 0.12)}`,
    position: "relative" as const,
    overflow: "hidden",
  },
  heroGlow: {
    position: "absolute" as const,
    top: rem(-80),
    right: rem(-80),
    width: rem(240),
    height: rem(240),
    borderRadius: "50%",
    background: alpha(theme.colors.cyan[5], 0.04),
    filter: "blur(60px)",
    pointerEvents: "none" as const,
  },

  // Navigation cards
  navCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    cursor: "pointer",
    transition: "all 200ms ease",
    "&:hover": {
      borderColor: theme.colors.dark[4],
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
      transform: "translateY(-2px)",
    },
  },
  navCardTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    color: theme.colors.dark[0],
  },
  navCardDesc: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[3],
    lineHeight: 1.6,
  },
  navCardArrow: {
    color: theme.colors.dark[4],
    transition: "color 200ms ease, transform 200ms ease",
  },

  // Quick stats
  quickStatCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    textAlign: "center" as const,
  },
  quickStatValue: {
    fontSize: rem(28),
    fontWeight: 800,
    lineHeight: 1,
    color: theme.colors.dark[0],
  },
  quickStatLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 500,
    color: theme.colors.dark[3],
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },

  sectionTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    color: theme.colors.dark[1],
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
}));

// ---------------------------------------------------------------------------
// Home Component
// ---------------------------------------------------------------------------

export const LimitsHome = () => {
  const { classes } = useHomeStyles();
  const navigate = useNavigate();

  const { summary: quickStats, loading } = useOrganizationLimits();

  return (
    <Stack gap="xl" className={`${classes.page} wp-fade-in`}>
      <PageHeader
        title="Billing & Limits"
        subtitle="Your subscription, resource limits, and payment settings at a glance"
        showBreadcrumbs={false}
      />

      {/* Hero */}
      <Card className={classes.heroCard} p="xl" radius="lg">
        <div className={classes.heroGlow} />
        <Stack gap="sm" style={{ position: "relative", zIndex: 1 }}>
          <Text fw={700} size="xl" c="dark.0">
            Welcome to your Billing Center
          </Text>
          <Text size="sm" c="dimmed" lh={1.7} maw={720}>
            This section lets you monitor resource usage, upgrade your plan with
            subscription packages, and configure payment collection through
            Stripe Connect. Everything you need to manage your organization's
            capacity and revenue is here.
          </Text>
        </Stack>
      </Card>

      {/* Quick Stats */}
      {!loading && (
        <Box>
          <Text className={classes.sectionTitle} mb="md">
            Health Overview
          </Text>
          <SimpleGrid
            cols={{ base: 2, xs: 4, md: 4 }}
            className="wp-stagger-children"
          >
            <Card className={classes.quickStatCard} p="md" radius="lg">
              <Stack gap={4} align="center">
                <Text className={classes.quickStatValue}>
                  {quickStats.total}
                </Text>
                <Text className={classes.quickStatLabel}>Total Limits</Text>
              </Stack>
            </Card>
            <Card className={classes.quickStatCard} p="md" radius="lg">
              <Stack gap={4} align="center">
                <Text className={classes.quickStatValue} c="teal.4">
                  {quickStats.healthy}
                </Text>
                <Text className={classes.quickStatLabel}>Healthy</Text>
              </Stack>
            </Card>
            <Card className={classes.quickStatCard} p="md" radius="lg">
              <Stack gap={4} align="center">
                <Text className={classes.quickStatValue} c="amber.4">
                  {quickStats.nearLimit}
                </Text>
                <Text className={classes.quickStatLabel}>Near Limit</Text>
              </Stack>
            </Card>
            <Card className={classes.quickStatCard} p="md" radius="lg">
              <Stack gap={4} align="center">
                <Text className={classes.quickStatValue} c="rose.4">
                  {quickStats.atCapacity}
                </Text>
                <Text className={classes.quickStatLabel}>At Capacity</Text>
              </Stack>
            </Card>
          </SimpleGrid>
        </Box>
      )}

      {/* Navigation Cards */}
      <Box>
        <Text className={classes.sectionTitle} mb="md">
          Manage
        </Text>
        <SimpleGrid
          cols={{ base: 1, sm: 3, md: 3 }}
          className="wp-stagger-children"
        >
          {/* Limits Card */}
          <Card
            className={classes.navCard}
            p="lg"
            radius="lg"
            onClick={() => navigate("/limits/list")}
          >
            <Stack gap="md">
              <Group justify="space-between" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="light" color="cyan">
                  <IconGauge size={20} />
                </ThemeIcon>
                <IconArrowRight size={18} className={classes.navCardArrow} />
              </Group>
              <Stack gap={4}>
                <Text className={classes.navCardTitle}>Resource Limits</Text>
                <Text className={classes.navCardDesc}>
                  View all your organization's limits including storage, users,
                  tournaments, and more. Monitor usage levels and identify
                  resources nearing their cap.
                </Text>
              </Stack>
            </Stack>
          </Card>

          {/* Subscription Card */}
          <Card
            className={classes.navCard}
            p="lg"
            radius="lg"
            onClick={() => navigate("/limits/subscription")}
          >
            <Stack gap="md">
              <Group justify="space-between" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="light" color="violet">
                  <IconSparkles size={20} />
                </ThemeIcon>
                <IconArrowRight size={18} className={classes.navCardArrow} />
              </Group>
              <Stack gap={4}>
                <Text className={classes.navCardTitle}>Subscription</Text>
                <Text className={classes.navCardDesc}>
                  Browse available packages to increase your limits. Manage your
                  monthly plan, apply coupon codes, and access your Stripe
                  billing portal.
                </Text>
              </Stack>
            </Stack>
          </Card>

          {/* Seller Card */}
          <Card
            className={classes.navCard}
            p="lg"
            radius="lg"
            onClick={() => navigate("/limits/seller")}
          >
            <Stack gap="md">
              <Group justify="space-between" align="flex-start">
                <ThemeIcon size="lg" radius="xl" variant="light" color="green">
                  <IconServer size={20} />
                </ThemeIcon>
                <IconArrowRight size={18} className={classes.navCardArrow} />
              </Group>
              <Stack gap={4}>
                <Text className={classes.navCardTitle}>Seller Settings</Text>
                <Text className={classes.navCardDesc}>
                  Connect your Stripe account to accept payments for event
                  tickets, shop items, and tournament entry fees. View your
                  current fees and payout configuration.
                </Text>
              </Stack>
            </Stack>
          </Card>
        </SimpleGrid>
      </Box>
    </Stack>
  );
};
